import { DateTime } from 'luxon';
import storageHelpers from '@satellite/helpers/storage';
import gateFeedService from '@satellite/services/gate-feed-service';

import i18n from '@/plugins/i18n.js';

import { mapObservations } from './mappers';

function makeInitialGateFeedData(state) {
  return {
    selectedWarehouseId: state.selectedWarehouse?.id ?? null
  };
}

function setGateFeedData(userId, key, data, state) {
  const novaGateFeedKey = storageHelpers.makeUserBoundedKey('nova_gate_feed', userId);

  if (!localStorage.getItem(novaGateFeedKey)) {
    localStorage.setItem(
      novaGateFeedKey,
      JSON.stringify({
        ...makeInitialGateFeedData(state)
      })
    );
  }

  let currentStorage = JSON.parse(localStorage.getItem(novaGateFeedKey));
  currentStorage[key] = data;

  localStorage.setItem(novaGateFeedKey, JSON.stringify({ ...currentStorage }));
}

// Handle initial date state
const DEFAULT_DATE_RANGE_DAYS = 7;
const DATE_NOW = DateTime.now();

const startOfDay = DATE_NOW.minus({ days: DEFAULT_DATE_RANGE_DAYS });
const endOfDay = DATE_NOW.endOf('day');

const defaultStartDate = startOfDay.toJSDate();
const defaultEndDate = endOfDay.toJSDate();

const state = {
  selectedWarehouse: null,
  observations: [],
  gates: [],
  gate: null,
  searchStr: null,
  date: [defaultStartDate, defaultEndDate],
  direction: null,
  isLoadingObservations: false,
  filters: {},
  tableOptions: {
    itemsPerPage: 10,
    offset: 0
  }
};

const getters = {};

const actions = {
  checkSelectedWarehouse({ state }) {
    if (!state.selectedWarehouse?.id) {
      this.notify(i18n.global.t('gateFeed.messages.pleaseSelectAWarehouseFirst'), 'error');
      return false;
    }

    return true;
  },
  persistSelectedWarehouse({ state, rootState }, warehouseId) {
    const userId = rootState.Auth.me.id;
    setGateFeedData(userId, 'selectedWarehouseId', warehouseId, state);
  },
  fetchPreviousGateFeedData({ rootState }) {
    const userId = rootState.Auth.me.id;
    const novaGateFeedKey = storageHelpers.makeUserBoundedKey('nova_gate_feed', userId);

    const data = localStorage.getItem(novaGateFeedKey);

    if (!data) {
      return null;
    }

    return JSON.parse(data);
  },
  async fetchObservations({ commit, state }) {
    if (!state.selectedWarehouse?.id) {
      console.warn('Failed to fetch observations: no selected warehouse');
      return;
    }
    commit('setIsLoadingObservations', true);
    try {
      const request = await gateFeedService.search(state.selectedWarehouse.id, {
        ...state.filters,
        pagination: {
          limit: state.tableOptions.itemsPerPage,
          offset: state.tableOptions.offset
        },
        sort: state.tableOptions.sort
      });
      const mappedObservations = mapObservations(request.items);
      commit('setObservations', { items: mappedObservations, metadata: request.meta });
    } catch (error) {
      this.notify('Failed to get observations', 'error');
      console.warn(error);
    } finally {
      commit('setIsLoadingObservations', false);
    }
  },
  async fetchGates({ commit, state }, params) {
    try {
      const warehouseId = params.warehouseId || state.selectedWarehouse.id;
      const gates = await gateFeedService.getGatesByWarehouseId(warehouseId);
      commit('setGates', gates);
    } catch (error) {
      this.notify('Failed to get Gates', 'error');
      console.warn(error);
      return [];
    }
  },
  async searchObservations({ commit, state }, { filters }) {
    if (!state.selectedWarehouse?.id) {
      console.warn('Failed to search observations: no selected warehouse');
      return;
    }
    commit('setFilters', filters);
    await this.dispatch('GateFeed/fetchObservations');
  }
};

const mutations = {
  setSelectedWarehouse(state, warehouse) {
    state.selectedWarehouse = warehouse;
  },
  setIsLoadingObservations(state, value) {
    state.isLoadingObservations = value;
  },
  setObservations(state, observations) {
    state.observations = observations;
  },
  setGates(state, gates) {
    state.gates = gates;
  },
  setGate(state, gate) {
    state.gate = gate;
  },
  setDate(state, date) {
    state.date = date;
  },
  setDirection(state, direction) {
    state.direction = direction;
  },
  setSearchStr(state, searchStr) {
    state.searchStr = searchStr;
  },
  setFilters(state, filters) {
    const selectedFilters = state.filters;

    if (filters.direction === null) {
      delete selectedFilters.direction;
    }
    if (filters.date === null) {
      delete selectedFilters.startDate;
      delete selectedFilters.endDate;
    }
    if (filters.gate === null) {
      delete selectedFilters.gateId;
    }
    if (filters.searchStr === null) {
      delete selectedFilters.search;
    }

    if (filters.direction) {
      selectedFilters.direction = filters.direction;
    }
    if (filters.date) {
      const startDate = DateTime.fromJSDate(filters.date[0]).startOf('day');
      const endDate = DateTime.fromJSDate(filters.date[1]).endOf('day');
      selectedFilters.startDate = startDate;
      selectedFilters.endDate = endDate;
    }
    if (filters.gate) {
      selectedFilters.gateId = filters.gate.id;
    }
    if (filters.searchStr) {
      selectedFilters.search = filters.searchStr;
    }

    state.filters = selectedFilters;
  },
  setTableOptions(state, { itemsPerPage, offset, sort }) {
    state.tableOptions.itemsPerPage = itemsPerPage;
    state.tableOptions.offset = offset;
    state.tableOptions.sort = sort;

    if (state.tableOptions.sort === null) {
      delete state.tableOptions.sort;
    }
  }
};

export default {
  namespaced: true,
  name: 'GateFeed',
  state,
  getters,
  actions,
  mutations
};
